import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Court,
  OrganizationSport,
  PlayEventCreateInput,
  PlayEventFormat,
  PlayEventSide,
  PlayGenderRestriction,
  PlayJoinPolicy,
  PlayPaymentMode,
} from '@court-sync/shared';
import {
  PLAY_EVENT_FORMATS,
  PLAY_EVENT_SIDES,
  PLAY_GENDER_RESTRICTIONS,
  PLAY_PAYMENT_MODES,
  addMinutesToDate,
  formatPlayEventDuration,
  DEFAULT_PAIRING_PRIORITIES,
  pricingParticipantTypesForJoinPolicy,
  availablePlayJoinPolicies,
} from '@court-sync/shared';
import { Info, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { SelectableCard, SelectableCardGrid } from '@/components/onboarding/SelectableCard';
import { cn } from '@/lib/utils';
import {
  Button,
  Checkbox,
  Drawer,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from '@/components/ui';
import { clubService } from '@/services/club';
import { organizationService } from '@/services/organization';
import { isApiError } from '@/services/apiClient';
import { playEventService } from '@/services/playEvent';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { uploadPlayEventImage } from '@/services/storage';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_GENDER_RESTRICTION_LABELS,
  PLAY_JOIN_POLICY_LABELS,
  PLAY_PAYMENT_MODE_LABELS,
} from '@/lib/playEventLabels';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { PLAY_EVENT_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';

const PLAY_EVENT_FORM_ID = 'play-event-create-form';

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDatetimeLocal(value: string): Date {
  return new Date(value);
}

function addDurationToStart(start: Date, hours: number, minutes: number): Date {
  return addMinutesToDate(start, hours * 60 + minutes);
}

function parsePriceToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function isTournamentPlayFormat(format: PlayEventFormat): boolean {
  return format === 'round_robin' || format === 'single_elimination';
}

interface PlayEventCreateDrawerProps {
  organizationId: string;
  clubId: string;
  timezone: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function PlayEventCreateDrawer({
  organizationId,
  clubId,
  timezone,
  isOpen,
  onClose,
  onCreated,
}: PlayEventCreateDrawerProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { startPageTour } = usePageTourActions();
  const { hasClubManagement, hasAcademyManagement } = useClubModuleAccess();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [drawerPanelOpen, setDrawerPanelOpen] = useState(false);
  const tourScheduledRef = useRef(false);
  const cancelTourScheduleRef = useRef<(() => void) | null>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [sports, setSports] = useState<OrganizationSport[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 2);
    return d;
  }, []);

  const [name, setName] = useState('');
  const [format, setFormat] = useState<PlayEventFormat>('social_rotate');
  const [side, setSide] = useState<PlayEventSide>('doubles');
  const [joinPolicy, setJoinPolicy] = useState<PlayJoinPolicy>('open');
  const [paymentMode, setPaymentMode] = useState<PlayPaymentMode>('at_door');
  const [genderRestriction, setGenderRestriction] = useState<PlayGenderRestriction>('open');
  const [memberPrice, setMemberPrice] = useState('');
  const [studentPrice, setStudentPrice] = useState('');
  const [guestPrice, setGuestPrice] = useState('');
  const [startsAt, setStartsAt] = useState(toDatetimeLocalValue(defaultStart));
  const [durationHours, setDurationHours] = useState('2');
  const [durationMinutes, setDurationMinutes] = useState('0');
  const [courtIds, setCourtIds] = useState<string[]>([]);
  const [organizationSportId, setOrganizationSportId] = useState('');
  const [capacity, setCapacity] = useState('');
  const [withdrawHoursBeforeStart, setWithdrawHoursBeforeStart] = useState('24');
  const [isFeatured, setIsFeatured] = useState(false);
  const [kioskRegistrationEnabled, setKioskRegistrationEnabled] = useState(false);
  const [kioskGuestRegistrationEnabled, setKioskGuestRegistrationEnabled] = useState(false);
  const [matchScoringEnabled, setMatchScoringEnabled] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState('');

  const {
    displayUrl: coverDisplayUrl,
    uploading: uploadingCover,
    handleFileSelect: handleCoverFileSelect,
    handleRemove: handleCoverRemove,
    clear: clearCoverUpload,
    resolveImagePayloadForSubmit: resolveCoverImagePayload,
  } = useCoverImageUpload({
    academyId: organizationId,
    imageUrl: coverImageUrl,
    setImageUrl: setCoverImageUrl,
    upload: uploadPlayEventImage,
  });

  const resetForm = () => {
    setName('');
    setFormat('social_rotate');
    setSide('doubles');
    setJoinPolicy('open');
    setPaymentMode('at_door');
    setGenderRestriction('open');
    setMemberPrice('');
    setStudentPrice('');
    setGuestPrice('');
    setStartsAt(toDatetimeLocalValue(defaultStart));
    setDurationHours('2');
    setDurationMinutes('0');
    setCourtIds([]);
    setCapacity('');
    setWithdrawHoursBeforeStart('24');
    setIsFeatured(false);
    setKioskRegistrationEnabled(false);
    setKioskGuestRegistrationEnabled(false);
    setMatchScoringEnabled(true);
    setCoverImageUrl('');
    clearCoverUpload();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    resetForm();
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [courtsRes, sportsRes] = await Promise.all([
          clubService.getClubCourts(clubId),
          organizationService.getOrganizationSports(organizationId),
        ]);
        if (cancelled) return;
        const courtList = courtsRes.success && courtsRes.data ? courtsRes.data : [];
        const sportList =
          sportsRes.success && sportsRes.data
            ? sportsRes.data.filter((sport) => sport.isActive)
            : [];
        setCourts(courtList);
        setSports(sportList);
        const primary = sportList.find((sport) => sport.isPrimary) ?? sportList[0];
        setOrganizationSportId(primary?.id ?? '');
      } catch (error) {
        if (!cancelled) {
          const message = isApiError(error)
            ? error.message
            : 'Failed to load courts and sports for this event';
          toast.error(message);
          setCourts([]);
          setSports([]);
          setOrganizationSportId('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [clubId, defaultStart, isOpen, organizationId]);

  const derivedEndLabel = useMemo(() => {
    if (!startsAt) return null;

    const hours = Number(durationHours);
    const minutes = Number(durationMinutes);
    if (
      !Number.isFinite(hours) ||
      hours < 0 ||
      !Number.isFinite(minutes) ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes < 1) return null;
    const end = addDurationToStart(parseDatetimeLocal(startsAt), hours, minutes);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      durationLabel: formatPlayEventDuration(totalMinutes),
      endLabel: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())} ${pad(end.getHours())}:${pad(end.getMinutes())}`,
    };
  }, [durationHours, durationMinutes, startsAt]);

  const pricingParticipantTypes = useMemo(
    () =>
      pricingParticipantTypesForJoinPolicy(joinPolicy, {
        hasClubManagement,
        hasAcademyManagement,
      }),
    [joinPolicy, hasClubManagement, hasAcademyManagement]
  );

  const availableJoinPolicies = useMemo(
    () =>
      availablePlayJoinPolicies({
        hasClubManagement,
        hasAcademyManagement,
      }),
    [hasClubManagement, hasAcademyManagement]
  );

  useEffect(() => {
    if (!availableJoinPolicies.includes(joinPolicy)) {
      setJoinPolicy('open');
    }
  }, [availableJoinPolicies, joinPolicy]);

  const pricingGridCols =
    pricingParticipantTypes.length === 1
      ? 'sm:grid-cols-1'
      : pricingParticipantTypes.length === 2
        ? 'sm:grid-cols-2'
        : 'sm:grid-cols-3';

  const selectedSport = sports.find((sport) => sport.id === organizationSportId);

  const toggleCourt = (courtId: string) => {
    setCourtIds((prev) =>
      prev.includes(courtId) ? prev.filter((id) => id !== courtId) : [...prev, courtId]
    );
  };

  const selectAllCourts = () => {
    setCourtIds(courts.map((court) => court.id));
  };

  const clearCourts = () => {
    setCourtIds([]);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedSport) {
      toast.error(
        sports.length === 0
          ? 'No sports are configured for this organization. Add sports in organization settings first.'
          : 'Select a sport for this event'
      );
      return;
    }
    if (!name.trim()) {
      toast.error('Event name is required');
      nameInputRef.current?.focus();
      nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (courtIds.length === 0) {
      toast.error('Select at least one court');
      return;
    }
    if (capacity.trim()) {
      const parsedCapacity = Number(capacity);
      if (!Number.isFinite(parsedCapacity) || parsedCapacity < 1) {
        toast.error('Capacity must be at least 1');
        return;
      }
    }

    const startDate = parseDatetimeLocal(startsAt);
    const hours = Number(durationHours);
    const minutes = Number(durationMinutes);
    if (
      !Number.isFinite(hours) ||
      hours < 0 ||
      !Number.isFinite(minutes) ||
      minutes < 0 ||
      minutes > 59
    ) {
      toast.error('Enter a valid duration');
      return;
    }
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes < 1) {
      toast.error('Duration must be at least 1 minute');
      return;
    }
    const endDate = addDurationToStart(startDate, hours, minutes);

    let parsedWithdrawHoursBeforeStart: number | undefined;
    if (isTournamentPlayFormat(format)) {
      const withdrawHours = Number(withdrawHoursBeforeStart);
      if (!Number.isFinite(withdrawHours) || withdrawHours < 0 || withdrawHours > 720) {
        toast.error('Withdraw deadline must be between 0 and 720 hours before start');
        return;
      }
      parsedWithdrawHoursBeforeStart = withdrawHours;
    }

    let resolvedCoverImageUrl: string | null | undefined;
    try {
      const coverPayload = resolveCoverImagePayload();
      if (coverPayload.imageUrl !== undefined) {
        resolvedCoverImageUrl = coverPayload.imageUrl;
      } else if (coverImageUrl.trim()) {
        resolvedCoverImageUrl = coverImageUrl.trim();
      }
    } catch {
      toast.error('Please wait for the cover image upload to finish');
      return;
    }

    const payload: PlayEventCreateInput = {
      name: name.trim(),
      sportId: selectedSport.sportId,
      academySportId: selectedSport.id,
      format,
      side,
      joinPolicy,
      paymentMode,
      genderRestriction,
      startsAt: startDate,
      endsAt: endDate,
      courtIds,
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      pairingPriorities: [...DEFAULT_PAIRING_PRIORITIES],
      matchupVisibilityMinutes: 15,
      nextMatchWithdrawMinutes: 10,
      isFeatured,
      kioskRegistrationEnabled,
      kioskGuestRegistrationEnabled: kioskRegistrationEnabled
        ? kioskGuestRegistrationEnabled
        : false,
      matchScoringEnabled,
      ...(resolvedCoverImageUrl !== undefined ? { coverImageUrl: resolvedCoverImageUrl } : {}),
      memberPriceCents: pricingParticipantTypes.includes('member')
        ? parsePriceToCents(memberPrice)
        : null,
      studentPriceCents: pricingParticipantTypes.includes('student')
        ? parsePriceToCents(studentPrice)
        : null,
      guestPriceCents: pricingParticipantTypes.includes('guest')
        ? parsePriceToCents(guestPrice)
        : null,
      ...(capacity.trim() ? { capacity: Number(capacity) } : {}),
      ...(parsedWithdrawHoursBeforeStart !== undefined
        ? { withdrawHoursBeforeStart: parsedWithdrawHoursBeforeStart }
        : {}),
      socialPlayMode: null,
      oddPlayerPolicy: null,
      roundCount: null,
      roundDurationMinutes: null,
      roundBreakMinutes: null,
    };

    setSubmitting(true);
    try {
      const res = await playEventService.createEvent(organizationId, payload);
      if (res.success && res.data?.id) {
        toast.success('Play event created');
        onCreated?.();
        onClose();
        navigate(`/admin/play-events/${res.data.id}`);
        return;
      }
      toast.error(res.error ?? 'Failed to create event');
    } catch (error) {
      const message = isApiError(error) ? error.message : 'Failed to create event';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateClick = () => {
    void handleSubmit({ preventDefault: () => undefined } as React.FormEvent);
  };

  useEffect(() => {
    if (!isOpen) {
      setDrawerPanelOpen(false);
      tourScheduledRef.current = false;
      cancelTourScheduleRef.current?.();
      cancelTourScheduleRef.current = null;
    }
  }, [isOpen]);

  const handleDrawerPanelOpen = useCallback(() => {
    setDrawerPanelOpen(true);
    if (
      !isOpen ||
      tourScheduledRef.current ||
      isPageTourComplete(user?.preferences, 'play-events-create')
    ) {
      return;
    }
    tourScheduledRef.current = true;
    cancelTourScheduleRef.current?.();
    cancelTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'play-events-create',
      user?.preferences,
    );
  }, [isOpen, startPageTour, user?.preferences]);

  usePageTourReady('play-events-create', isOpen && drawerPanelOpen && !loading);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="New play event"
      description="Create a draft event. Publish it when you are ready to open registration."
      width="2xl"
      panelId={PLAY_EVENT_CREATE_TOUR_STEP_IDS.DRAWER}
      onPanelOpen={handleDrawerPanelOpen}
      headerActions={
        <DrawerTourHelp
          tourKey="play-events-create"
          suggestion="Creating a play event? Tour this form for format, schedule, registration, and courts."
          buttonLabel="Tour this form"
        />
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.SUBMIT}
            type="button"
            onClick={handleCreateClick}
            isLoading={submitting}
            disabled={loading || submitting || courts.length === 0}
          >
            Create draft event
          </Button>
        </div>
      }
    >
      <form
        id={PLAY_EVENT_FORM_ID}
        className="space-y-6"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
      >
        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : (
          <>
          <div id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.BASICS} className="space-y-6">
          <Field>
            <FieldLabel htmlFor="play-event-name">Name</FieldLabel>
            <Input
              ref={nameInputRef}
              id="play-event-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Friday night social"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel>Format</FieldLabel>
              <Select value={format} onValueChange={(v) => setFormat(v as PlayEventFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_EVENT_FORMATS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_EVENT_FORMAT_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>Side</FieldLabel>
              <Select value={side} onValueChange={(v) => setSide(v as PlayEventSide)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_EVENT_SIDES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_EVENT_SIDE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel>Gender restriction</FieldLabel>
              <Select
                value={genderRestriction}
                onValueChange={(v) => setGenderRestriction(v as PlayGenderRestriction)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_GENDER_RESTRICTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_GENDER_RESTRICTION_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          </div>

          <div id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.SCHEDULE} className="space-y-6">
          <Field>
            <FieldLabel htmlFor="play-event-starts">Starts</FieldLabel>
            <Input
              id="play-event-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="play-event-duration-hours">Duration (hours)</FieldLabel>
              <Input
                id="play-event-duration-hours"
                type="number"
                min={0}
                step={1}
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-event-duration-minutes">Duration (minutes)</FieldLabel>
              <Input
                id="play-event-duration-minutes"
                type="number"
                min={0}
                max={59}
                step={1}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
              />
            </Field>
          </div>

          {isTournamentPlayFormat(format) ? (
            <Field>
              <div className="flex items-center gap-1.5">
                <FieldLabel htmlFor="play-event-withdraw-hours" className="w-auto">
                  Withdraw deadline (hours before start)
                </FieldLabel>
                <Tooltip
                  content="After this deadline, participants can no longer withdraw themselves and must contact your club. You can still withdraw them from the roster, but online payments are not refunded automatically."
                  side="top"
                >
                  <button
                    type="button"
                    className="inline-flex rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="What happens after the withdraw deadline"
                  >
                    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </button>
                </Tooltip>
              </div>
              <FieldDescription>
                Participants can withdraw until this many hours before the event starts.
              </FieldDescription>
              <Input
                id="play-event-withdraw-hours"
                type="number"
                min={0}
                max={720}
                step={1}
                value={withdrawHoursBeforeStart}
                onChange={(e) => setWithdrawHoursBeforeStart(e.target.value)}
              />
            </Field>
          ) : null}

          {derivedEndLabel ? (
            <p className="text-sm text-muted-foreground">
              Event duration {derivedEndLabel.durationLabel} · Ends at {derivedEndLabel.endLabel}
            </p>
          ) : null}
          </div>

          <div id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.REGISTRATION} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel>Join policy</FieldLabel>
              <Select value={joinPolicy} onValueChange={(v) => setJoinPolicy(v as PlayJoinPolicy)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableJoinPolicies.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_JOIN_POLICY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Payment</FieldLabel>
              <Select
                value={paymentMode}
                onValueChange={(v) => setPaymentMode(v as PlayPaymentMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_PAYMENT_MODES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_PAYMENT_MODE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className={cn('grid gap-4', pricingGridCols)}>
            {pricingParticipantTypes.includes('member') ? (
              <Field>
                <FieldLabel htmlFor="play-event-member-price">Member price</FieldLabel>
                <Input
                  id="play-event-member-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={memberPrice}
                  onChange={(e) => setMemberPrice(e.target.value)}
                  placeholder="Free"
                />
              </Field>
            ) : null}
            {pricingParticipantTypes.includes('student') ? (
              <Field>
                <FieldLabel htmlFor="play-event-student-price">Student price</FieldLabel>
                <Input
                  id="play-event-student-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={studentPrice}
                  onChange={(e) => setStudentPrice(e.target.value)}
                  placeholder="Free"
                />
              </Field>
            ) : null}
            {pricingParticipantTypes.includes('guest') ? (
              <Field>
                <FieldLabel htmlFor="play-event-guest-price">Guest price</FieldLabel>
                <Input
                  id="play-event-guest-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={guestPrice}
                  onChange={(e) => setGuestPrice(e.target.value)}
                  placeholder="Free"
                />
              </Field>
            ) : null}
          </div>
          <FieldDescription>Leave pricing blank for free entry. Amounts are per participant.</FieldDescription>

          <Field>
            <FieldLabel htmlFor="play-event-capacity">Capacity (optional)</FieldLabel>
            <Input
              id="play-event-capacity"
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="Unlimited"
            />
          </Field>

          {sports.length > 1 ? (
            <Field>
              <FieldLabel>Sport</FieldLabel>
              <Select value={organizationSportId} onValueChange={setOrganizationSportId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.customLabel ?? sport.sportId}
                      {sport.isPrimary ? ' (primary)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
          </div>

          <div id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.COURTS} className="space-y-2">
          <Field>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <FieldLabel className="w-auto">
                Courts
                {courtIds.length > 0 ? (
                  <span className="font-normal text-muted-foreground">
                    {' '}
                    ({courtIds.length} selected)
                  </span>
                ) : null}
              </FieldLabel>
              {courts.length > 1 ? (
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                    onClick={selectAllCourts}
                  >
                    Select all
                  </button>
                  <span className="text-muted-foreground" aria-hidden>
                    ·
                  </span>
                  <button
                    type="button"
                    className={cn(
                      'font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
                      courtIds.length === 0
                        ? 'cursor-not-allowed text-muted-foreground/60'
                        : 'text-primary'
                    )}
                    onClick={clearCourts}
                    disabled={courtIds.length === 0}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>
            <FieldDescription>
              Courts are reserved for the full event window.
            </FieldDescription>
            {courts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                No courts found. Add courts under Courts &amp; locations first.
              </div>
            ) : (
              <SelectableCardGrid label="Courts" selectionMode="multiple">
                {courts.map((court) => (
                  <SelectableCard
                    key={court.id}
                    id={`play-event-court-${court.id}`}
                    title={court.name}
                    selected={courtIds.includes(court.id)}
                    onSelect={() => toggleCourt(court.id)}
                    selectionMode="multiple"
                    icon={<MapPin className="h-4 w-4" aria-hidden />}
                  />
                ))}
              </SelectableCardGrid>
            )}
          </Field>
          </div>

          <div id={PLAY_EVENT_CREATE_TOUR_STEP_IDS.OPTIONS} className="space-y-6">
          <LessonCoverImageField
            inputId="play-event-cover-image"
            displayUrl={coverDisplayUrl}
            uploading={uploadingCover}
            disabled={submitting}
            onFileSelect={handleCoverFileSelect}
            onRemove={handleCoverRemove}
            description="Shown on your public play events page. Max 5MB, images only."
          />

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={isFeatured}
              onCheckedChange={(v) => setIsFeatured(Boolean(v))}
            />
            <span>Feature on public home page</span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={matchScoringEnabled}
              onCheckedChange={(v) => setMatchScoringEnabled(Boolean(v))}
            />
            <span>
              Record match scores
              <span className="mt-1 block text-muted-foreground">
                When enabled, operators can record results and set scores from the match draw.
              </span>
            </span>
          </label>

          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={kioskRegistrationEnabled}
                onCheckedChange={(v) => {
                  const enabled = Boolean(v);
                  setKioskRegistrationEnabled(enabled);
                  if (!enabled) {
                    setKioskGuestRegistrationEnabled(false);
                  }
                }}
              />
              <span>
                Allow kiosk registration
                <span className="mt-1 block text-muted-foreground">
                  Members and students can register on the day at the event kiosk. Ideal for social
                  events where last-minute drop-ins should not disrupt the roster.
                </span>
              </span>
            </label>

            {kioskRegistrationEnabled && pricingParticipantTypes.includes('guest') ? (
              <label className="ml-7 flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={kioskGuestRegistrationEnabled}
                  onCheckedChange={(v) => setKioskGuestRegistrationEnabled(Boolean(v))}
                />
                <span>
                  Allow guest registration at kiosk
                  <span className="mt-1 block text-muted-foreground">
                    Walk-in guests can register and check in from the kiosk when this is enabled.
                  </span>
                </span>
              </label>
            ) : null}
          </div>
          </div>
          </>
        )}
      </form>
    </Drawer>
  );
}
